import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import { DatabaseContext } from '../../redux/DatabaseStore';
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { getRequest } from '../../api/ApiController';
import { getServiceGraphDeadlineColors, getServiceGraphStatesColors, getServiceGraphPeriodColors } from '../../utils/moduleSettings';
import { Chart } from 'primereact/chart';
import { Calendar } from '../../components/PrimeCalendar';
import { Skeleton } from 'primereact/skeleton';
import { Message } from 'primereact/message';
import { addLocale } from 'primereact/api';
import { RefreshIcon } from '../svg/svgIcons';
import { LocaleContext } from '../../components/LocaleContext';
import { hasCrmModuleAccess, hasCrmModuleCompleteList } from '../../utils/crmCommunicationPermissions';
import {
  CRM_COMM_MASK_COMPLETE_LIST,
  CRM_COMM_MASK_MY_AGENDA,
} from './CrmCommunicationTicketsList';

const CALENDAR_DAY_CODES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const CALENDAR_MONTH_CODES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getYesterdayISO() {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function toLocalDateString(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getDateRange(timePeriod, date) {
  if (!date) return { from: null, to: null };
  const lastDayStr = getYesterdayISO();
  let from, to;
  if (timePeriod === 'Za deň') {
    const d = new Date(date);
    const s = toLocalDateString(d);
    from = s;
    to = s;
  } else if (timePeriod === 'Za mesiac') {
    const d = new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(y, d.getMonth() + 1, 0).getDate();
    from = `${y}-${m}-01`;
    to = `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
  } else if (timePeriod === 'Za rok') {
    const y = new Date(date).getFullYear();
    from = `${y}-01-01`;
    to = `${y}-12-31`;
  } else if (timePeriod === 'Vlastné' && Array.isArray(date) && date.length >= 2) {
    from = toLocalDateString(new Date(date[0]));
    to = toLocalDateString(new Date(date[1]));
  } else {
    return { from: null, to: null };
  }
  // Obmedzenie na súčasnosť: nepočítame budúce dni ani dnešok
  if (to && to > lastDayStr) to = lastDayStr;
  if (from && to && from > to) return { from: lastDayStr, to: lastDayStr };
  return { from, to };
}

const PERIOD_OPTIONS = ['Všetko', 'Za deň', 'Za mesiac', 'Za rok', 'Vlastné'];
const PREVIEW_PAGE = 'CrmCommunicationPreview';

function parseSavedPeriod(attrs) {
  if (!attrs?.preview_period || typeof attrs.preview_period.timePeriod !== 'string') return null;
  if (!PERIOD_OPTIONS.includes(attrs.preview_period.timePeriod)) return null;
  const { timePeriod } = attrs.preview_period;
  const date = attrs.preview_period.date;
  if (timePeriod === 'Všetko') return { timePeriod, date: null };
  if (timePeriod === 'Vlastné' && Array.isArray(date) && date.length >= 2) {
    return { timePeriod, date: date.map((d) => (d != null ? new Date(d) : null)) };
  }
  if (date != null) {
    const d = new Date(date);
    if (!isNaN(d.getTime())) return { timePeriod, date: d };
  }
  return null;
}

function serializePeriod(timePeriod, date) {
  const payload = { timePeriod };
  if (date == null) payload.date = null;
  else if (Array.isArray(date)) payload.date = date.map((d) => (d instanceof Date ? toLocalDateString(d) : d));
  else payload.date = date instanceof Date ? toLocalDateString(date) : date;
  return payload;
}

export default function PreviewCrmCommunication() {
  const { t, locale } = useContext(LocaleContext);
  const [calendarLocale, setCalendarLocale] = useState('sk');
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { setUserMaskFilter } = useContext(UtilContext);
  const [timePeriod, setTimePeriod] = useState('Všetko');
  const [date, setDate] = useState(null);
  const [dateProps, setDateProps] = useState({ hidden: true });
  const appliedSavedRef = useRef(false);
  const [maskReady, setMaskReady] = useState(false);
  const [stats, setStats] = useState({ moje_otvorene: null, moje_pridelene: null, uplny: null });
  const [statsByMonth, setStatsByMonth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [legendHidden, setLegendHidden] = useState({
    moje_otvorene: [], moje_pridelene: [], uplny: [],
    moje_otvorene_period: [], moje_pridelene_period: [], uplny_period: [],
    by_month_my_tickets: [], by_month_my_tasks: [], by_month_full: [],
  });
  const originalDataRef = useRef({
    moje_otvorene: [], moje_pridelene: [], uplny: [],
    moje_otvorene_period: [], moje_pridelene_period: [], uplny_period: [],
    by_month_my_tickets: [], by_month_my_tasks: [], by_month_full: [],
  });
  const mojeOtvoreneChartRef = useRef(null);
  const mojePrideleneChartRef = useRef(null);
  const uplnyChartRef = useRef(null);
  const mojeOtvorenePeriodChartRef = useRef(null);
  const mojePridelenePeriodChartRef = useRef(null);
  const uplnyPeriodChartRef = useRef(null);
  const byMonthMyTicketsChartRef = useRef(null);
  const byMonthMyTasksChartRef = useRef(null);
  const byMonthFullChartRef = useRef(null);

  const canAccessCrmAgenda = hasCrmModuleAccess(user);
  const canAccessComplete = hasCrmModuleCompleteList(user) || user?.is_superuser;
  const periodOptionLabels = useMemo(
    () => ({
      'Všetko': t('ServiceTaskPreview.PeriodOption.All'),
      'Za deň': t('ServiceTaskPreview.PeriodOption.Day'),
      'Za mesiac': t('ServiceTaskPreview.PeriodOption.Month'),
      'Za rok': t('ServiceTaskPreview.PeriodOption.Year'),
      'Vlastné': t('ServiceTaskPreview.PeriodOption.Custom'),
    }),
    [t]
  );

  useEffect(() => {
    const activeLocale = locale === 'gb' ? 'en' : (locale || 'sk');
    addLocale(activeLocale, {
      firstDayOfWeek: 1,
      showMonthAfterYear: false,
      dayNames: CALENDAR_DAY_CODES.map((d) => t(`Common.Day.${d}`)),
      dayNamesShort: CALENDAR_DAY_CODES.map((d) => t(`Common.DayShort.${d}`)),
      dayNamesMin: CALENDAR_DAY_CODES.map((d) => t(`Common.DayMin.${d}`)),
      monthNames: CALENDAR_MONTH_CODES.map((m) => t(`Common.Month.${m}`)),
      monthNamesShort: CALENDAR_MONTH_CODES.map((m) => t(`Common.MonthShort.${m}`)),
      today: t('Common.Today'),
      clear: t('Common.Clear'),
      chooseDate: t('Common.ChooseDate'),
      chooseMonth: t('Common.ChooseMonth'),
      chooseYear: t('Common.ChooseYear'),
      prevMonth: t('Common.PrevMonth'),
      nextMonth: t('Common.NextMonth'),
      prevYear: t('Common.PrevYear'),
      nextYear: t('Common.NextYear'),
      prevHour: t('Common.PrevHour'),
      nextHour: t('Common.NextHour'),
      prevMinute: t('Common.PrevMinute'),
      nextMinute: t('Common.NextMinute'),
      prevSecond: t('Common.PrevSecond'),
      nextSecond: t('Common.NextSecond'),
      am: t('Common.Am'),
      pm: t('Common.Pm'),
      weekHeader: t('Common.WeekHeader'),
    });
    setCalendarLocale(activeLocale);
  }, [locale, t]);

  useEffect(() => {
    if (!state.user_mask_filters) return; // ešte nenačítané
    if (appliedSavedRef.current) {
      setMaskReady(true);
      return;
    }
    if (!state.user_mask_filters.length) {
      setMaskReady(true);
      return;
    }
    appliedSavedRef.current = true;
    const mask = state.user_mask_filters.find((m) => m.page === PREVIEW_PAGE);
    const attrs = mask?.attributes;
    const parsed = typeof attrs === 'string' ? (() => { try { return parseSavedPeriod(JSON.parse(attrs)); } catch { return null; } })() : parseSavedPeriod(attrs);
    if (parsed) {
      setTimePeriod(parsed.timePeriod);
      setDate(parsed.date ?? (parsed.timePeriod === 'Všetko' ? null : new Date()));
      if (parsed.timePeriod === 'Za deň') setDateProps({ dateFormat: 'dd.mm.yy' });
      else if (parsed.timePeriod === 'Za mesiac') setDateProps({ view: 'month', dateFormat: 'mm.yy' });
      else if (parsed.timePeriod === 'Za rok') setDateProps({ view: 'year', dateFormat: 'yy' });
      else if (parsed.timePeriod === 'Vlastné') setDateProps({ dateFormat: 'dd.mm.yy', selectionMode: 'range' });
      else setDateProps({ hidden: true });
    }
    setMaskReady(true);
  }, [state.user_mask_filters]);

  const savePeriodToDb = () => {
    setUserMaskFilter(PREVIEW_PAGE, 'set_attributes', null, null, { preview_period: serializePeriod(timePeriod, date) });
  };

  const fetchStats = (from, to, yearMonth = null) => {
    setLoading(true);
    if (yearMonth) {
      const params = new URLSearchParams({ year: yearMonth.year, month: yearMonth.month });
      getRequest(`/api/crm_communication_preview_stats_by_month?${params.toString()}`)
        .then((resp) => {
          setStatsByMonth(resp?.data ?? null);
          setStats({ moje_otvorene: null, moje_pridelene: null, uplny: null });
        })
        .catch(() => setStatsByMonth(null))
        .finally(() => setLoading(false));
      return;
    }
    setStatsByMonth(null);
    const params = new URLSearchParams();
    if (from) params.set('from_date', from);
    if (to) params.set('to_date', to);
    getRequest(`/api/crm_communication_preview_stats?${params.toString()}`)
      .then((resp) => {
        setStats(resp?.data ?? { moje_otvorene: null, moje_pridelene: null, uplny: null });
      })
      .catch(() => setStats({ moje_otvorene: null, moje_pridelene: null, uplny: null }))
      .finally(() => setLoading(false));
  };

  // Refetch when user changes; volať až po aplikovaní masky (aby sa nevolal 2× – raz bez a raz s periodou)
  useEffect(() => {
    if (!user) {
      setStats({ moje_otvorene: null, moje_pridelene: null, uplny: null });
      setStatsByMonth(null);
      return;
    }
    if (!maskReady) return;
    if (timePeriod === 'Všetko' || !date) {
      fetchStats(null, null);
    } else if (timePeriod === 'Za mesiac' && date) {
      const d = new Date(date);
      fetchStats(null, null, { year: d.getFullYear(), month: d.getMonth() + 1 });
    } else {
      const { from, to } = getDateRange(timePeriod, date);
      fetchStats(from, to);
    }
  }, [timePeriod, date, user?.id, maskReady]);

  useEffect(() => {
    const mo = stats.moje_otvorene;
    const mp = stats.moje_pridelene;
    const up = stats.uplny;
    const mop = stats.moje_otvorene_period;
    const mpp = stats.moje_pridelene_period;
    const up_p = stats.uplny_period;
    const bm = statsByMonth;
    setLegendHidden((prev) => ({
      ...prev,
      moje_otvorene: [], moje_pridelene: [], uplny: [],
      moje_otvorene_period: mop ? [] : prev.moje_otvorene_period,
      moje_pridelene_period: mpp ? [] : prev.moje_pridelene_period,
      uplny_period: up_p ? [] : prev.uplny_period,
      by_month_my_tickets: bm?.my_tickets ? [] : prev.by_month_my_tickets,
      by_month_my_tasks: bm?.my_tasks ? [] : prev.by_month_my_tasks,
      by_month_full: bm?.full_list ? [] : prev.by_month_full,
    }));
    originalDataRef.current = {
      ...originalDataRef.current,
      moje_otvorene: mo
        ? [mo.caka_na_schvalenie, mo.schvaleny, mo.v_rieseni, mo.hotove, mo.caka_na_kontrolu, mo.znovuotvoreny]
        : [],
      moje_pridelene: mp ? [mp.po_termine, mp.blizi_sa, mp.ok] : [],
      uplny: up ? [up.po_termine, up.blizi_sa, up.ok] : [],
      moje_otvorene_period: mop ? [mop.pribudlo, mop.nevyriesene, mop.ukoncene] : [],
      moje_pridelene_period: mpp ? [mpp.pribudlo, mpp.nevyriesene, mpp.ukoncene] : [],
      uplny_period: up_p ? [up_p.pribudlo, up_p.nevyriesene, up_p.ukoncene] : [],
      by_month_my_tickets: bm?.my_tickets ? [bm.my_tickets.created, bm.my_tickets.closed, bm.my_tickets.open_end] : [],
      by_month_my_tasks: bm?.my_tasks ? [bm.my_tasks.assigned, bm.my_tasks.completed, bm.my_tasks.open_end] : [],
      by_month_full: bm?.full_list ? [bm.full_list.created, bm.full_list.closed, bm.full_list.open_end] : [],
    };
  }, [stats, statsByMonth]);

  function changeTimePeriod(time_period) {
    setTimePeriod(time_period);
    setDate(null);
    if (time_period === 'Za deň') setDateProps({ dateFormat: 'dd.mm.yy' });
    else if (time_period === 'Za mesiac') setDateProps({ view: 'month', dateFormat: 'mm.yy' });
    else if (time_period === 'Za rok') setDateProps({ view: 'year', dateFormat: 'yy' });
    else if (time_period === 'Vlastné') setDateProps({ dateFormat: 'dd.mm.yy', selectionMode: 'range' });
    else setDateProps({ hidden: true });
  }

  function showResult(newDate) {
    setDate(newDate);
  }

  function handleRefresh() {
    if (timePeriod === 'Všetko' || !date) {
      fetchStats(null, null);
    } else if (timePeriod === 'Za mesiac' && date) {
      const d = new Date(date);
      fetchStats(null, null, { year: d.getFullYear(), month: d.getMonth() + 1 });
    } else {
      const { from, to } = getDateRange(timePeriod, date);
      fetchStats(from, to);
    }
  }

  const plugin = {
    id: 'emptyDoughnut',
    afterDraw(chart) {
      const data = chart.data?.datasets?.[0]?.data ?? [];
      const count = data.reduce((a, b) => a + (b ?? 0), 0);
      if (count < 1) {
        const ctx = chart.ctx;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '20px Arial';
        ctx.fillStyle = '#9ca3af';
        ctx.fillText(t('ServiceTaskPreview.NoRecords'), chart.width / 2, chart.height / 2);
      }
    },
  };

  const openList = (page, titleKey, mask, preview, scope = null) => {
    dispatch({
      type: 'create_new_tab',
      name: t(titleKey),
      translationKey: titleKey,
      legacyLabel: t(titleKey),
      page,
      props: { preview, timePeriod, date, scope },
      mask,
    });
  };

  const originalTotal = (chartKey, fallbackArr) => {
    const hidden = legendHidden[chartKey] ?? [];
    const originals = originalDataRef.current[chartKey];
    if (Array.isArray(originals)) {
      return originals.reduce((acc, val, i) => acc + (hidden[i] ? 0 : Number(val) || 0), 0);
    }
    if (fallbackArr?.length) {
      return fallbackArr.reduce((a, b) => a + (Number(b) || 0), 0);
    }
    return 0;
  };

  const toggleLegendItem = (chartKey, index, chartRef) => () => {
    const chartApi = chartRef?.current;
    const ch = chartApi?.getChart?.();
    if (!ch) return;
    let originals = originalDataRef.current[chartKey];
    if (!Array.isArray(originals) || originals.length === 0) {
      const src = ch.data?.datasets?.[0]?.data;
      if (!Array.isArray(src)) return;
      originals = src.map((v) => Number(v) || 0);
      originalDataRef.current[chartKey] = originals;
    }
    const currentlyHidden = legendHidden[chartKey]?.[index];
    const nextHidden = !currentlyHidden;
    const hiddenArr = legendHidden[chartKey] ?? [];
    const newData = originals.map((val, i) => {
      const isHidden = i === index ? nextHidden : hiddenArr[i];
      return isHidden ? 0 : (Number(val) || 0);
    });
    const ds = ch.data.datasets[0];
    ds.data = [...newData];
    const meta = ch.getDatasetMeta?.(0);
    if (meta && Array.isArray(meta._parsed)) newData.forEach((v, i) => { meta._parsed[i] = v; });
    ch.update();
    setLegendHidden((prev) => {
      const arr = prev[chartKey] ?? [];
      const next = [...arr];
      next[index] = nextHidden;
      return { ...prev, [chartKey]: next };
    });
  };

  const chartStatesColors = getServiceGraphStatesColors(state.module_settings || []);
  const chartDeadlineColors = getServiceGraphDeadlineColors(state.module_settings || []);
  const chartPeriodColors = getServiceGraphPeriodColors(state.module_settings || []);

  const mojeOtvoreneData = useMemo(
    () =>
      stats.moje_otvorene && {
      labels: [
          t('ServiceTask.Status.WaitingApproval'),
          t('ServiceTask.Status.Approved'),
          t('ServiceTask.Status.InProgress'),
          t('ServiceTask.Status.Done'),
          t('ServiceTask.Status.WaitingInspection'),
          t('ServiceTask.Status.Reopened'),
        ],
        datasets: [
          {
            data: [
              stats.moje_otvorene.caka_na_schvalenie,
              stats.moje_otvorene.schvaleny,
              stats.moje_otvorene.v_rieseni,
              stats.moje_otvorene.hotove,
              stats.moje_otvorene.caka_na_kontrolu,
              stats.moje_otvorene.znovuotvoreny,
            ],
            backgroundColor: chartStatesColors,
            hoverBackgroundColor: chartStatesColors,
          },
        ],
      },
    [stats.moje_otvorene, chartStatesColors]
  );

  const mojeOtvoreneOptions = {
    plugins: { legend: { display: false } },
    onClick(evt, item) {
      if (!item?.length) return;
      const map = {
        0: 'caka_na_schvalenie',
        1: 'schvaleny',
        2: 'v_rieseni',
        3: 'hotove',
        4: 'caka_na_kontrolu',
        5: 'znovuotvoreny',
      };
      openList('CrmCommunicationMyAgendaList', 'ServiceTaskPreview.MyOpenTickets', CRM_COMM_MASK_MY_AGENDA, map[item[0].index], 'moje_tikety');
    },
  };

  const mojePrideleneData = useMemo(
    () =>
      stats.moje_pridelene && {
        labels: [t('ServiceTask.Status.Overdue'), t('ServiceTask.Status.DeadlineSoon'), t('ServiceGeneralSettings.Done')],
        datasets: [
          {
            data: [
              stats.moje_pridelene.po_termine,
              stats.moje_pridelene.blizi_sa,
              stats.moje_pridelene.ok,
            ],
            backgroundColor: chartDeadlineColors,
            hoverBackgroundColor: chartDeadlineColors,
          },
        ],
      },
    [stats.moje_pridelene, chartDeadlineColors]
  );

  const mojePrideleneOptions = {
    plugins: { legend: { display: false } },
    onClick(evt, item) {
      if (!item?.length) return;
      const map = { 0: 'po_termine', 1: 'blizi_sa', 2: 'ok' };
      openList('CrmCommunicationMyAgendaList', 'ServiceTaskPreview.MyOpenTasks', CRM_COMM_MASK_MY_AGENDA, map[item[0].index], 'moje_ulohy');
    },
  };

  const uplnyData = useMemo(
    () =>
      stats.uplny && {
        labels: [t('ServiceTask.Status.Overdue'), t('ServiceTask.Status.DeadlineSoon'), t('ServiceGeneralSettings.Done')],
        datasets: [
          {
            data: [stats.uplny.po_termine, stats.uplny.blizi_sa, stats.uplny.ok],
            backgroundColor: chartDeadlineColors,
            hoverBackgroundColor: chartDeadlineColors,
          },
        ],
      },
    [stats.uplny, chartDeadlineColors]
  );

  const uplnyOptions = {
    plugins: { legend: { display: false } },
    onClick(evt, item) {
      if (!item?.length) return;
      const map = { 0: 'po_termine', 1: 'blizi_sa', 2: 'ok' };
      openList('CrmCommunicationCompleteList', 'ServiceTaskPreview.FullRequestList', CRM_COMM_MASK_COMPLETE_LIST, map[item[0].index]);
    },
  };

  const PERIOD_LABELS = [t('ServiceTask.Period.AddedTickets'), t('ServiceTask.Period.UnresolvedTickets'), t('ServiceTask.Period.ClosedTickets')];
  const BY_MONTH_TICKETS_LABELS = [t('ServiceTaskPreview.Month.CreatedTickets'), t('ServiceTaskPreview.Month.ClosedTickets'), t('ServiceTaskPreview.Month.UnclosedTickets')];
  const BY_MONTH_TASKS_LABELS = [t('ServiceTaskPreview.Month.AssignedTasks'), t('ServiceTaskPreview.Month.CompletedTasks'), t('ServiceTaskPreview.Month.UncompletedTasks')];
  const periodSegmentMap = { 0: 'pribudlo', 1: 'nevyriesene', 2: 'ukoncene' };

  const mojeOtvorenePeriodData = useMemo(
    () =>
      stats.moje_otvorene_period && {
        labels: PERIOD_LABELS,
        datasets: [
          {
            data: [
              stats.moje_otvorene_period.pribudlo,
              stats.moje_otvorene_period.nevyriesene,
              stats.moje_otvorene_period.ukoncene,
            ],
            backgroundColor: chartPeriodColors,
            hoverBackgroundColor: chartPeriodColors,
          },
        ],
      },
    [stats.moje_otvorene_period, chartPeriodColors]
  );

  const mojePridelenePeriodData = useMemo(
    () =>
      stats.moje_pridelene_period && {
        labels: PERIOD_LABELS,
        datasets: [
          {
            data: [
              stats.moje_pridelene_period.pribudlo,
              stats.moje_pridelene_period.nevyriesene,
              stats.moje_pridelene_period.ukoncene,
            ],
            backgroundColor: chartPeriodColors,
            hoverBackgroundColor: chartPeriodColors,
          },
        ],
      },
    [stats.moje_pridelene_period, chartPeriodColors]
  );

  const uplnyPeriodData = useMemo(
    () =>
      stats.uplny_period && {
        labels: PERIOD_LABELS,
        datasets: [
          {
            data: [
              stats.uplny_period.pribudlo,
              stats.uplny_period.nevyriesene,
              stats.uplny_period.ukoncene,
            ],
            backgroundColor: chartPeriodColors,
            hoverBackgroundColor: chartPeriodColors,
          },
        ],
      },
    [stats.uplny_period, chartPeriodColors]
  );

  const byMonthMyTicketsData = useMemo(
    () =>
      statsByMonth?.my_tickets && {
        labels: BY_MONTH_TICKETS_LABELS,
        datasets: [
          {
            data: [
              Number(statsByMonth.my_tickets.created) || 0,
              Number(statsByMonth.my_tickets.closed) || 0,
              Number(statsByMonth.my_tickets.open_end) || 0,
            ],
            backgroundColor: chartPeriodColors,
            hoverBackgroundColor: chartPeriodColors,
          },
        ],
      },
    [statsByMonth?.my_tickets, chartPeriodColors]
  );

  const byMonthMyTasksData = useMemo(
    () =>
      statsByMonth?.my_tasks && {
        labels: BY_MONTH_TASKS_LABELS,
        datasets: [
          {
            data: [
              Number(statsByMonth.my_tasks.assigned) || 0,
              Number(statsByMonth.my_tasks.completed) || 0,
              Number(statsByMonth.my_tasks.open_end) || 0,
            ],
            backgroundColor: chartPeriodColors,
            hoverBackgroundColor: chartPeriodColors,
          },
        ],
      },
    [statsByMonth?.my_tasks, chartPeriodColors]
  );

  const byMonthFullData = useMemo(
    () =>
      statsByMonth?.full_list && {
        labels: BY_MONTH_TICKETS_LABELS,
        datasets: [
          {
            data: [
              Number(statsByMonth.full_list.created) || 0,
              Number(statsByMonth.full_list.closed) || 0,
              Number(statsByMonth.full_list.open_end) || 0,
            ],
            backgroundColor: chartPeriodColors,
            hoverBackgroundColor: chartPeriodColors,
          },
        ],
      },
    [statsByMonth?.full_list, chartPeriodColors]
  );

  const makePeriodChartOptions = (page, titleKey, mask, scope) => ({
    plugins: { legend: { display: false } },
    onClick(evt, item) {
      if (!item?.length) return;
      const seg = periodSegmentMap[item[0].index];
      openList(page, titleKey, mask, seg, scope);
    },
  });

  useEffect(() => {
    const d = (src) => (src?.datasets?.[0]?.data ? [...src.datasets[0].data] : []);
    originalDataRef.current = {
      moje_otvorene: d(mojeOtvoreneData),
      moje_pridelene: d(mojePrideleneData),
      uplny: d(uplnyData),
    };
  }, [mojeOtvoreneData, mojePrideleneData, uplnyData]);

  if (!canAccessCrmAgenda) {
    return (
      <div className="relative flex flex-col bg-gray-100 w-full py-4 px-4">
        <Message severity="warn" text={t('CrmCommunication.List.ModuleAccessDenied')} />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-137px)]">
      <div className="lg:grid grid-cols-3 gap-1">
        <div />
        <div>
          <div className="flex items-center gap-3 mt-4">
            <label htmlFor="time_period" className="text-xl font-semibold text-gray-800 shrink-0">
              {t('ServiceTaskPreview.Period')}:
            </label>
            <select
              name="time_period"
              value={timePeriod}
              onChange={(e) => changeTimePeriod(e.target.value)}
              className="bg-white flex-1 min-w-0 px-4 py-2 text-gray-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
            >
              <option value="Všetko">{periodOptionLabels['Všetko']}</option>
              <option value="Za deň">{periodOptionLabels['Za deň']}</option>
              <option value="Za mesiac">{periodOptionLabels['Za mesiac']}</option>
              <option value="Za rok">{periodOptionLabels['Za rok']}</option>
              <option value="Vlastné">{periodOptionLabels['Vlastné']}</option>
            </select>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              title={t('Common.RefreshData')}
              className="flex items-center justify-center w-10 h-10 shrink-0 rounded-md border border-gray-300 bg-gray-600 hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none text-white transition-colors"
            >
              <RefreshIcon />
            </button>
            <button
              type="button"
              onClick={savePeriodToDb}
              title={t('ServiceTaskPreview.SavePeriodHint')}
              className="shrink-0 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring focus:ring-gray-200"
            >
              {t('Common.SaveMask')}
            </button>
          </div>
        </div>
        <div />
        <div />
        <div className="flex-auto">
          <Calendar
            value={date}
            onChange={(e) => showResult(e.target.value)}
            showIcon
            className="w-full"
            locale={calendarLocale}
            {...dateProps}
            hideOnRangeSelection
            style={{ border: '1px solid #e5e7eb', borderRadius: '6px' }}
          />
        </div>
        <div />
      </div>

      {loading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
              <div className="text-center mb-4"><Skeleton width="12rem" height="1.5rem" className="mx-auto" /></div>
              <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
                <Skeleton shape="circle" size="320px" className="flex-shrink-0" />
              </div>
              <div className="text-center mt-4"><Skeleton width="8rem" height="1.5rem" className="mx-auto" /></div>
            </div>
          ))}
        </div>
      )}

      {!loading && (() => {
        const useByMonthView = timePeriod === 'Za mesiac' && date != null && statsByMonth != null;
        const usePeriodView = !useByMonthView && timePeriod !== "Všetko" && date != null
          && (stats.moje_otvorene_period != null || stats.moje_pridelene_period != null || stats.uplny_period != null);

        const byMonthSegmentMap = { 0: 'pribudlo', 1: 'ukoncene', 2: 'nevyriesene' };

        if (useByMonthView) {
          return (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
              {canAccessCrmAgenda && statsByMonth.my_tickets && (
                <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                  <h2 className="text-center text-xl font-semibold mb-4">{t('ServiceTaskPreview.MyTickets')}</h2>
                  <div className="w-full max-w-[420px] h-[420px] mx-auto">
                    <Chart
                      ref={byMonthMyTicketsChartRef}
                      type="doughnut"
                      data={byMonthMyTicketsData || { labels: [], datasets: [{ data: [] }] }}
                      options={{
                        plugins: { legend: { display: false } },
                        onClick(evt, item) {
                          if (!item?.length) return;
                          const seg = byMonthSegmentMap[item[0].index];
                          openList('CrmCommunicationMyAgendaList', 'PreviewCrmCommunication.MyAgendaTitle', CRM_COMM_MASK_MY_AGENDA, seg, 'moje_tikety');
                        },
                      }}
                      className="w-full h-full"
                      plugins={[plugin]}
                    />
                  </div>
                  <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 mt-2 text-sm text-gray-700">
                    {(byMonthMyTicketsData?.labels ?? []).map((label, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={toggleLegendItem('by_month_my_tickets', i, byMonthMyTicketsChartRef)}
                        className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80"
                        style={{
                          textDecoration: legendHidden.by_month_my_tickets?.[i] ? 'line-through' : undefined,
                          opacity: legendHidden.by_month_my_tickets?.[i] ? 0.5 : 1,
                        }}
                      >
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: chartPeriodColors[i] ?? '#6b7280' }} />
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="text-center font-semibold mt-4">
                    {t('PreviewServiceTask.TotalLabel')}: {(statsByMonth.my_tickets?.created ?? 0) + (statsByMonth.my_tickets?.closed ?? 0) + (statsByMonth.my_tickets?.open_end ?? 0)}
                  </div>
                </div>
              )}
              {canAccessCrmAgenda && statsByMonth.my_tasks && (
                <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                  <h2 className="text-center text-xl font-semibold mb-4">{t('ServiceTaskPreview.MyTasks')}</h2>
                  <div className="w-full max-w-[420px] h-[420px] mx-auto">
                    <Chart
                      ref={byMonthMyTasksChartRef}
                      type="doughnut"
                      data={byMonthMyTasksData || { labels: [], datasets: [{ data: [] }] }}
                      options={{
                        plugins: { legend: { display: false } },
                        onClick(evt, item) {
                          if (!item?.length) return;
                          const seg = byMonthSegmentMap[item[0].index];
                          openList('CrmCommunicationMyAgendaList', 'PreviewCrmCommunication.MyAgendaTitle', CRM_COMM_MASK_MY_AGENDA, seg, 'moje_ulohy');
                        },
                      }}
                      className="w-full h-full"
                      plugins={[plugin]}
                    />
                  </div>
                  <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 mt-2 text-sm text-gray-700">
                    {(byMonthMyTasksData?.labels ?? []).map((label, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={toggleLegendItem('by_month_my_tasks', i, byMonthMyTasksChartRef)}
                        className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80"
                        style={{
                          textDecoration: legendHidden.by_month_my_tasks?.[i] ? 'line-through' : undefined,
                          opacity: legendHidden.by_month_my_tasks?.[i] ? 0.5 : 1,
                        }}
                      >
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: chartPeriodColors[i] ?? '#6b7280' }} />
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="text-center font-semibold mt-4">
                    {t('PreviewServiceTask.TotalLabel')}: {(statsByMonth.my_tasks?.assigned ?? 0) + (statsByMonth.my_tasks?.completed ?? 0) + (statsByMonth.my_tasks?.open_end ?? 0)}
                  </div>
                </div>
              )}
              {canAccessComplete && statsByMonth.full_list && (
                <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                  <h2 className="text-center text-xl font-semibold mb-4">{t('ServiceTaskPreview.FullRequestList')}</h2>
                  <div className="w-full max-w-[420px] h-[420px] mx-auto">
                    <Chart
                      ref={byMonthFullChartRef}
                      type="doughnut"
                      data={byMonthFullData || { labels: [], datasets: [{ data: [] }] }}
                      options={{
                        plugins: { legend: { display: false } },
                        onClick(evt, item) {
                          if (!item?.length) return;
                          const seg = byMonthSegmentMap[item[0].index];
                          openList('CrmCommunicationCompleteList', 'ServiceTaskPreview.FullRequestList', CRM_COMM_MASK_COMPLETE_LIST, seg);
                        },
                      }}
                      className="w-full h-full"
                      plugins={[plugin]}
                    />
                  </div>
                  <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 mt-2 text-sm text-gray-700">
                    {(byMonthFullData?.labels ?? []).map((label, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={toggleLegendItem('by_month_full', i, byMonthFullChartRef)}
                        className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80"
                        style={{
                          textDecoration: legendHidden.by_month_full?.[i] ? 'line-through' : undefined,
                          opacity: legendHidden.by_month_full?.[i] ? 0.5 : 1,
                        }}
                      >
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: chartPeriodColors[i] ?? '#6b7280' }} />
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="text-center font-semibold mt-4">
                    {t('PreviewServiceTask.TotalLabel')}: {(statsByMonth.full_list?.created ?? 0) + (statsByMonth.full_list?.closed ?? 0) + (statsByMonth.full_list?.open_end ?? 0)}
                  </div>
                </div>
              )}
            </div>
          );
        }

        if (usePeriodView) {
          return (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
              {canAccessCrmAgenda && stats.moje_otvorene_period != null && (
                <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                  <h2 className="text-center text-xl font-semibold mb-4">{t('ServiceTaskPreview.MyTickets')}</h2>
                  <div className="w-full max-w-[420px] h-[420px] mx-auto">
                    <Chart
                      ref={mojeOtvorenePeriodChartRef}
                      type="doughnut"
                      data={mojeOtvorenePeriodData || { labels: [], datasets: [{ data: [] }] }}
                      options={makePeriodChartOptions('CrmCommunicationMyAgendaList', 'PreviewCrmCommunication.MyAgendaTitle', CRM_COMM_MASK_MY_AGENDA, 'moje_tikety')}
                      className="w-full h-full"
                      plugins={[plugin]}
                    />
                  </div>
                  <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 mt-2 text-sm text-gray-700">
                    {(mojeOtvorenePeriodData?.labels ?? []).map((label, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={toggleLegendItem('moje_otvorene_period', i, mojeOtvorenePeriodChartRef)}
                        className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80"
                        style={{
                          textDecoration: legendHidden.moje_otvorene_period?.[i] ? 'line-through' : undefined,
                          opacity: legendHidden.moje_otvorene_period?.[i] ? 0.5 : 1,
                        }}
                      >
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: chartPeriodColors[i] ?? '#6b7280' }} />
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="text-center font-semibold mt-4">
                    {t('PreviewServiceTask.TotalLabel')}: {originalTotal('moje_otvorene_period', mojeOtvorenePeriodData?.datasets?.[0]?.data)}
                  </div>
                </div>
              )}
              {canAccessCrmAgenda && stats.moje_pridelene_period != null && (
                <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                  <h2 className="text-center text-xl font-semibold mb-4">{t('ServiceTaskPreview.MyTasks')}</h2>
                  <div className="w-full max-w-[420px] h-[420px] mx-auto">
                    <Chart
                      ref={mojePridelenePeriodChartRef}
                      type="doughnut"
                      data={mojePridelenePeriodData || { labels: [], datasets: [{ data: [] }] }}
                      options={makePeriodChartOptions('CrmCommunicationMyAgendaList', 'PreviewCrmCommunication.MyAgendaTitle', CRM_COMM_MASK_MY_AGENDA, 'moje_ulohy')}
                      className="w-full h-full"
                      plugins={[plugin]}
                    />
                  </div>
                  <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 mt-2 text-sm text-gray-700">
                    {(mojePridelenePeriodData?.labels ?? []).map((label, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={toggleLegendItem('moje_pridelene_period', i, mojePridelenePeriodChartRef)}
                        className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80"
                        style={{
                          textDecoration: legendHidden.moje_pridelene_period?.[i] ? 'line-through' : undefined,
                          opacity: legendHidden.moje_pridelene_period?.[i] ? 0.5 : 1,
                        }}
                      >
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: chartPeriodColors[i] ?? '#6b7280' }} />
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="text-center font-semibold mt-4">
                    {t('PreviewServiceTask.TotalLabel')}: {originalTotal('moje_pridelene_period', mojePridelenePeriodData?.datasets?.[0]?.data)}
                  </div>
                </div>
              )}
              {canAccessComplete && stats.uplny_period != null && (
                <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                  <h2 className="text-center text-xl font-semibold mb-4">{t('ServiceTaskPreview.FullRequestList')}</h2>
                  <div className="w-full max-w-[420px] h-[420px] mx-auto">
                    <Chart
                      ref={uplnyPeriodChartRef}
                      type="doughnut"
                      data={uplnyPeriodData || { labels: [], datasets: [{ data: [] }] }}
                      options={makePeriodChartOptions('CrmCommunicationCompleteList', 'ServiceTaskPreview.FullRequestList', CRM_COMM_MASK_COMPLETE_LIST, null)}
                      className="w-full h-full"
                      plugins={[plugin]}
                    />
                  </div>
                  <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 mt-2 text-sm text-gray-700">
                    {(uplnyPeriodData?.labels ?? []).map((label, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={toggleLegendItem('uplny_period', i, uplnyPeriodChartRef)}
                        className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80"
                        style={{
                          textDecoration: legendHidden.uplny_period?.[i] ? 'line-through' : undefined,
                          opacity: legendHidden.uplny_period?.[i] ? 0.5 : 1,
                        }}
                      >
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: chartPeriodColors[i] ?? '#6b7280' }} />
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="text-center font-semibold mt-4">
                    {t('PreviewServiceTask.TotalLabel')}: {originalTotal('uplny_period', uplnyPeriodData?.datasets?.[0]?.data)}
                  </div>
                </div>
              )}
            </div>
          );
        }

        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
            {canAccessCrmAgenda && (
              <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                <h2 className="text-center text-xl font-semibold mb-4">{t('ServiceTaskPreview.MyOpenTickets')}</h2>
                <div className="w-full max-w-[420px] h-[420px] mx-auto">
                  <Chart
                    ref={mojeOtvoreneChartRef}
                    type="doughnut"
                    data={mojeOtvoreneData || { labels: [], datasets: [{ data: [] }] }}
                    options={mojeOtvoreneOptions}
                    className="w-full h-full"
                    plugins={[plugin]}
                  />
                </div>
                <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 mt-2 text-sm text-gray-700">
                  {(mojeOtvoreneData?.labels ?? []).map((label, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={toggleLegendItem('moje_otvorene', i, mojeOtvoreneChartRef)}
                      className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80"
                      style={{
                        textDecoration: legendHidden.moje_otvorene?.[i] ? 'line-through' : undefined,
                        opacity: legendHidden.moje_otvorene?.[i] ? 0.5 : 1,
                      }}
                    >
                      <span
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{
                          backgroundColor:
                            mojeOtvoreneData?.datasets?.[0]?.backgroundColor?.[i] ?? '#6b7280',
                        }}
                      />
                      {label}
                    </button>
                  ))}
                </div>
                <div className="text-center font-semibold mt-4">
                  {t('PreviewServiceTask.TotalLabel')}: {originalTotal('moje_otvorene', mojeOtvoreneData?.datasets?.[0]?.data)}
                </div>
              </div>
            )}

            {canAccessCrmAgenda && (
              <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                <h2 className="text-center text-xl font-semibold mb-4">{t('ServiceTaskPreview.MyOpenTasks')}</h2>
                <div className="w-full max-w-[420px] h-[420px] mx-auto">
                  <Chart
                    ref={mojePrideleneChartRef}
                    type="doughnut"
                    data={mojePrideleneData || { labels: [], datasets: [{ data: [] }] }}
                    options={mojePrideleneOptions}
                    className="w-full h-full"
                    plugins={[plugin]}
                  />
                </div>
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
                  {(mojePrideleneData?.labels ?? []).map((label, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={toggleLegendItem('moje_pridelene', i, mojePrideleneChartRef)}
                      className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80"
                      style={{
                        textDecoration: legendHidden.moje_pridelene?.[i] ? 'line-through' : undefined,
                        opacity: legendHidden.moje_pridelene?.[i] ? 0.5 : 1,
                      }}
                    >
                      <span
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{
                          backgroundColor:
                            mojePrideleneData?.datasets?.[0]?.backgroundColor?.[i] ?? '#6b7280',
                        }}
                      />
                      {label}
                    </button>
                  ))}
                </div>
                <div className="text-center font-semibold mt-4">
                  {t('PreviewServiceTask.TotalLabel')}: {originalTotal('moje_pridelene', mojePrideleneData?.datasets?.[0]?.data)}
                </div>
              </div>
            )}

            {canAccessComplete && (
              <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                <h2 className="text-center text-xl font-semibold mb-4">{t('ServiceTaskPreview.FullRequestList')}</h2>
                <div className="w-full max-w-[420px] h-[420px] mx-auto">
                  <Chart
                    ref={uplnyChartRef}
                    type="doughnut"
                    data={uplnyData || { labels: [], datasets: [{ data: [] }] }}
                    options={uplnyOptions}
                    className="w-full h-full"
                    plugins={[plugin]}
                  />
                </div>
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
                  {(uplnyData?.labels ?? []).map((label, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={toggleLegendItem('uplny', i, uplnyChartRef)}
                      className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80"
                      style={{
                        textDecoration: legendHidden.uplny?.[i] ? 'line-through' : undefined,
                        opacity: legendHidden.uplny?.[i] ? 0.5 : 1,
                      }}
                    >
                      <span
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{
                          backgroundColor: uplnyData?.datasets?.[0]?.backgroundColor?.[i] ?? '#6b7280',
                        }}
                      />
                      {label}
                    </button>
                  ))}
                </div>
                <div className="text-center font-semibold mt-4">
                  {t('PreviewServiceTask.TotalLabel')}: {originalTotal('uplny', uplnyData?.datasets?.[0]?.data)}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
